import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { format, subDays, subMonths, isValid, isSameDay } from 'date-fns';

/**
 * Preset definitions — maps a preset key to the number of days/months to go back.
 */
const PRESET_CONFIG = {
  week: { days: 7, label: 'Week' },
  '15days': { days: 15, label: '15 Days' },
  month: { days: 30, label: 'Month' },
  '3months': { months: 3, label: '3 Months' },
  custom: { label: 'Custom' },
};

/**
 * Computes the start date for a given preset, ending at `today`.
 * @param {string} preset - One of 'week', '15days', 'month', '3months'
 * @returns {{ startDate: Date, endDate: Date }}
 */
const computePresetRange = (preset) => {
  const endDate = new Date();

  switch (preset) {
    case 'week':
      return { startDate: subDays(endDate, 7), endDate };
    case '15days':
      return { startDate: subDays(endDate, 15), endDate };
    case 'month':
      return { startDate: subDays(endDate, 30), endDate };
    case '3months':
      return { startDate: subMonths(endDate, 3), endDate };
    default:
      return { startDate: subDays(endDate, 30), endDate };
  }
};

/**
 * Earliest selectable date across every Reports date picker (Custom Range
 * popover, Export modal). Data before this date isn't available.
 */
export const REPORTS_MIN_DATE = new Date('2025-01-01');

// Longest Custom range on every Reports tab, both ends counted. The PnL
// daywise API rejects ranges over 366 days.
export const REPORTS_MAX_RANGE_DAYS = 365;

const useReportFilters = (config = {}) => {
  const {
    defaultPreset = 'week',
    extraFilterDefs = [],
    onFiltersApplied,
  } = config;

  // ── Refs ──────────────────────────────────────────────────────
  // Keep onFiltersApplied in a ref so it doesn't cause re-renders.
  const onFiltersAppliedRef = useRef(onFiltersApplied);
  onFiltersAppliedRef.current = onFiltersApplied;

  // ── Date State ────────────────────────────────────────────────
  const initialRange = computePresetRange(defaultPreset);
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [selectedPreset, setSelectedPreset] = useState(defaultPreset);

  // ── Extra Filters State (dynamic, per tab) ────────────────────
  const buildDefaultExtras = useCallback(() => {
    const defaults = {};
    extraFilterDefs.forEach((def) => {
      defaults[def.key] = def.default;
    });
    return defaults;
  }, [extraFilterDefs]);

  const [extraFilters, setExtraFilters] = useState(() => buildDefaultExtras());

  const setExtraFilter = useCallback((key, value) => {
    setExtraFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Filter Drawer State ───────────────────────────────────────
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // ── Draft State for Drawer ──────────────────────────────────────
  const [draftExtraFilters, setDraftExtraFilters] = useState(() => buildDefaultExtras());

  const setDraftExtraFilter = useCallback((key, value) => {
    setDraftExtraFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Sync draft with committed when drawer opens
  useEffect(() => {
    if (filterDrawerOpen) {
      setDraftExtraFilters(extraFilters);
    }
  }, [filterDrawerOpen, extraFilters]);


  // ── Custom Date Popover State ─────────────────────────────────
  const [customPopoverAnchor, setCustomPopoverAnchor] = useState(null);

  // ── Micro-Filter State (clicked day in calendar) ──────────────
  const [selectedDay, setSelectedDay] = useState(null);

  // ── Derived: Active Filter Count (non-default extras) ─────────
  const activeFilterCount = useMemo(() => {
    let count = 0;
    extraFilterDefs.forEach((def) => {
      if (extraFilters[def.key] !== def.default) {
        count++;
      }
    });
    return count;
  }, [extraFilters, extraFilterDefs]);

  // ── Helper: Build the current filter state snapshot ────────────
  const getFilterState = useCallback(
    (overrides = {}) => {
      return {
        startDate: overrides.startDate || startDate,
        endDate: overrides.endDate || endDate,
        selectedPreset: overrides.selectedPreset || selectedPreset,
        selectedDay: overrides.selectedDay !== undefined ? overrides.selectedDay : selectedDay,
        ...extraFilters,
        ...overrides,
      };
    },
    [startDate, endDate, selectedPreset, selectedDay, extraFilters]
  );

  // ── Notify: Fire the callback with current state ──────────────
  const notifyFiltersApplied = useCallback(
    (overrides = {}) => {
      if (onFiltersAppliedRef.current) {
        onFiltersAppliedRef.current(getFilterState(overrides));
      }
    },
    [getFilterState]
  );

  // ── Handlers ──────────────────────────────────────────────────

  /**
   * Handle a preset button click (Week, 15 Days, Month, etc.)
   */
  const handlePresetClick = useCallback(
    (preset) => {
      if (preset === 'custom') {
        // Custom is handled by the popover — just mark it as selected.
        setSelectedPreset('custom');
        return;
      }

      const range = computePresetRange(preset);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
      setSelectedPreset(preset);
      setSelectedDay(null); // Clear any micro-filter

      notifyFiltersApplied({
        startDate: range.startDate,
        endDate: range.endDate,
        selectedPreset: preset,
        selectedDay: null,
      });
    },
    [notifyFiltersApplied]
  );

  /**
   * Apply custom date range from the popover.
   */
  const handleCustomDateApply = useCallback(
    (newStartDate, newEndDate) => {
      if (!newStartDate || !newEndDate || !isValid(newStartDate) || !isValid(newEndDate)) {
        return;
      }
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      setSelectedPreset('custom');
      setSelectedDay(null);
      setCustomPopoverAnchor(null);

      notifyFiltersApplied({
        startDate: newStartDate,
        endDate: newEndDate,
        selectedPreset: 'custom',
        selectedDay: null,
      });
    },
    [notifyFiltersApplied]
  );

  /**
   * Apply filters from the drawer (for extra filters like Exchange, Buy/Sell, etc.)
   */
  const handleApplyFilters = useCallback(() => {
    setFilterDrawerOpen(false);
    setSelectedDay(null);
    setExtraFilters(draftExtraFilters); // Commit drafts
    notifyFiltersApplied({ ...draftExtraFilters, selectedDay: null });
  }, [notifyFiltersApplied, draftExtraFilters]);

  /**
   * Commit and apply a single extra filter immediately (for inline controls
   * outside the drawer, e.g. an Exchange dropdown in the filter bar).
   */
  const applyExtraFilter = useCallback(
    (key, value) => {
      setExtraFilters((prev) => ({ ...prev, [key]: value }));
      setSelectedDay(null);
      notifyFiltersApplied({ [key]: value, selectedDay: null });
    },
    [notifyFiltersApplied]
  );

  /**
   * Reset everything to defaults.
   */
  const handleResetFilters = useCallback(() => {
    const range = computePresetRange(defaultPreset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setSelectedPreset(defaultPreset);
    const resetExtras = buildDefaultExtras();
    setExtraFilters(resetExtras);
    setSelectedDay(null);
    setFilterDrawerOpen(false);
    setCustomPopoverAnchor(null);

    notifyFiltersApplied({
      startDate: range.startDate,
      endDate: range.endDate,
      selectedPreset: defaultPreset,
      selectedDay: null,
      ...resetExtras,
    });
  }, [defaultPreset, buildDefaultExtras, notifyFiltersApplied]);

  /**
   * Handle clicking a day in the calendar heatmap (micro-filter toggle).
   */
  const handleDayClick = useCallback(
    (date) => {
      if (!date || !isValid(date)) return;

      // Toggle: if same day is clicked again, clear the micro-filter.
      if (selectedDay && isSameDay(selectedDay, date)) {
        setSelectedDay(null);
        notifyFiltersApplied({ selectedDay: null });
      } else {
        setSelectedDay(date);
        notifyFiltersApplied({ selectedDay: date });
      }
    },
    [selectedDay, notifyFiltersApplied]
  );

  // ── Derived: Active Filter Pills ──────────────────────────────
  const activeFilterPills = useMemo(() => {
    const pills = [];

    // Date range pill (always shown)
    if (startDate && endDate && isValid(startDate) && isValid(endDate)) {
      pills.push({
        key: '_period',
        label: 'Period',
        displayValue: `${format(startDate, 'dd MMM')} – ${format(endDate, 'dd MMM yyyy')}`,
        onClear: selectedPreset === defaultPreset ? undefined : () => {
          // Resetting period resets to default preset
          const range = computePresetRange(defaultPreset);
          setStartDate(range.startDate);
          setEndDate(range.endDate);
          setSelectedPreset(defaultPreset);
          notifyFiltersApplied({
            startDate: range.startDate,
            endDate: range.endDate,
            selectedPreset: defaultPreset,
          });
        },
      });
    }

    // Extra filter pills (always shown, but un-clearable if default)
    extraFilterDefs.forEach((def) => {
      const currentValue = extraFilters[def.key];
      const displayValue = def.displayFn ? def.displayFn(currentValue) : String(currentValue);

      pills.push({
        key: def.key,
        label: def.label,
        hideLabel: def.hideLabel,
        displayValue,
        onClear: currentValue === def.default ? undefined : () => {
          setExtraFilter(def.key, def.default);
          // Immediately notify with the cleared value
          notifyFiltersApplied({ [def.key]: def.default });
        },
      });
    });

    // Micro-filter pill (selected day)
    if (selectedDay && isValid(selectedDay)) {
      pills.push({
        key: '_selectedDay',
        label: 'Selected Day',
        displayValue: format(selectedDay, 'dd MMM yyyy'),
        onClear: () => {
          setSelectedDay(null);
          notifyFiltersApplied({ selectedDay: null });
        },
      });
    }

    return pills;
  }, [
    startDate,
    endDate,
    extraFilters,
    extraFilterDefs,
    selectedDay,
    selectedPreset,
    defaultPreset,
    notifyFiltersApplied,
    setExtraFilter,
  ]);

  // ── Return ────────────────────────────────────────────────────
  return {
    // Date state
    startDate,
    endDate,
    selectedPreset,

    // Preset actions
    handlePresetClick,
    handleCustomDateApply,
    presets: PRESET_CONFIG,

    // Extra filters
    extraFilters,
    applyExtraFilter,
    draftExtraFilters,
    setDraftExtraFilter,

    // Filter drawer
    filterDrawerOpen,
    setFilterDrawerOpen,
    activeFilterCount,

    // Custom date popover
    customPopoverAnchor,
    setCustomPopoverAnchor,

    // Active filter pills
    activeFilterPills,

    // Micro-filtering
    selectedDay,
    handleDayClick,

    // Actions
    handleApplyFilters,
    handleResetFilters,

    // Helpers
    getFilterState,
  };
};

export default useReportFilters;
